/**
 * Client service for sector operations places and QR tokens.
 * R39 A2.a (plan docs/plans/sector-operations.md §4, §5, §6 A2.a).
 * Wraps callables for writes and listens to firestore ops_places for reads.
 */

import {
  collection,
  query,
  where,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type { OpsSubscriptionAccessOptions } from './opsRostersService'
import type {
  OpsPlace,
  OpsSector,
  CreatePlaceInput,
  RenamePlaceInput,
  RenamePlaceResult,
  SetPlacePresetInput,
  SetPlacePresetResult,
  SetPropertyRoomTypesInput,
  SetPropertyRoomTypesResult,
  SetRoomTypeInput,
  SetRoomTypeResult,
  MovePlaceInput,
  ArchivePlaceInput,
  BulkCreatePlacesInput,
  BulkCreatePlacesResult,
  CreatePlaceTokenInput,
  CreatePlaceTokenResult,
  RevokePlaceTokenInput,
  ResolvePlaceTokenInput,
  ResolvedPlaceToken,
  SetPlaceAccessInput,
  SetPlaceAccessResult,
  SetPlaceDutyChainInput,
  SetPlaceDutyChainResult,
  OpsPlaceAccessJobProgress,
} from '../types/ops'

export async function createPlace(
  input: CreatePlaceInput,
): Promise<{ placeId: string; path: string[] }> {
  return callable<CreatePlaceInput, { placeId: string; path: string[] }>('createPlace', input)
}

export async function renamePlace(
  input: RenamePlaceInput,
): Promise<RenamePlaceResult> {
  return callable<RenamePlaceInput, RenamePlaceResult>('renamePlace', input)
}

export async function setPlacePreset(
  input: SetPlacePresetInput,
): Promise<SetPlacePresetResult> {
  return callable<SetPlacePresetInput, SetPlacePresetResult>('setPlacePreset', input)
}

export async function setPropertyRoomTypes(
  input: SetPropertyRoomTypesInput,
): Promise<SetPropertyRoomTypesResult> {
  return callable<SetPropertyRoomTypesInput, SetPropertyRoomTypesResult>(
    'setPropertyRoomTypes',
    input,
  )
}

export async function setRoomType(
  input: SetRoomTypeInput,
): Promise<SetRoomTypeResult> {
  return callable<SetRoomTypeInput, SetRoomTypeResult>('setRoomType', input)
}

export async function movePlace(
  input: MovePlaceInput,
): Promise<{ success: boolean; placeId: string; path: string[]; updatedCount: number }> {
  return callable<MovePlaceInput, { success: boolean; placeId: string; path: string[]; updatedCount: number }>(
    'movePlace',
    input,
  )
}

export async function archivePlace(
  input: ArchivePlaceInput,
): Promise<{ success: boolean; placeId: string; archived: boolean }> {
  return callable<ArchivePlaceInput, { success: boolean; placeId: string; archived: boolean }>(
    'archivePlace',
    input,
  )
}

export async function bulkCreatePlaces(
  input: BulkCreatePlacesInput,
): Promise<BulkCreatePlacesResult> {
  return callable<BulkCreatePlacesInput, BulkCreatePlacesResult>('bulkCreatePlaces', input)
}

export async function createPlaceToken(
  input: CreatePlaceTokenInput,
): Promise<CreatePlaceTokenResult> {
  return callable<CreatePlaceTokenInput, CreatePlaceTokenResult>('createPlaceToken', input)
}

export async function revokePlaceToken(
  input: RevokePlaceTokenInput,
): Promise<CreatePlaceTokenResult> {
  return callable<RevokePlaceTokenInput, CreatePlaceTokenResult>('revokePlaceToken', input)
}

export async function resolvePlaceToken(
  input: ResolvePlaceTokenInput,
): Promise<ResolvedPlaceToken> {
  return callable<ResolvePlaceTokenInput, ResolvedPlaceToken>('resolvePlaceToken', input)
}

export type BackfillOpsPlaceReadAccessInput = {
  workspaceId: string
  dryRun?: boolean
}

export type BackfillOpsPlaceReadAccessResult = {
  scanned: number
  missing: number
  updated: number
  dryRun: boolean
}

export async function backfillOpsPlaceReadAccess(
  input: BackfillOpsPlaceReadAccessInput,
): Promise<BackfillOpsPlaceReadAccessResult> {
  return callable<BackfillOpsPlaceReadAccessInput, BackfillOpsPlaceReadAccessResult>(
    'backfillOpsPlaceReadAccess',
    input,
  )
}

export async function setPlaceAccess(
  input: SetPlaceAccessInput,
): Promise<SetPlaceAccessResult> {
  return callable<SetPlaceAccessInput, SetPlaceAccessResult>('setPlaceAccess', input)
}

export async function setPlaceDutyChain(
  input: SetPlaceDutyChainInput,
): Promise<SetPlaceDutyChainResult> {
  return callable<SetPlaceDutyChainInput, SetPlaceDutyChainResult>('setPlaceDutyChain', input)
}

function mapPlaceDoc(doc: { id: string; data: () => Record<string, unknown> }): OpsPlace {
  const data = doc.data()
  return {
    id: doc.id,
    workspaceId: data.workspaceId as string,
    sector: data.sector as OpsSector,
    parentId: (data.parentId as string | null) ?? null,
    path: Array.isArray(data.path) ? (data.path as string[]) : [doc.id],
    level: data.level as OpsPlace['level'],
    name: data.name as string,
    code: data.code as string,
    ownerRoleKey: (data.ownerRoleKey as string | null) ?? null,
    dutyChain: (data.dutyChain as OpsPlace['dutyChain']) ?? null,
    departmentKey: (data.departmentKey as string | null) ?? null,
    preset: (data.preset as OpsPlace['preset']) ?? null,
    zoneKind: (data.zoneKind as OpsPlace['zoneKind']) ?? null,
    blockKind: (data.blockKind as OpsPlace['blockKind']) ?? null,
    areaKind: (data.areaKind as OpsPlace['areaKind']) ?? null,
    roomTypes: Array.isArray(data.roomTypes) ? (data.roomTypes as OpsPlace['roomTypes']) : undefined,
    roomTypeId: (data.roomTypeId as string | null) ?? null,
    attendanceGraceMinutes: typeof data.attendanceGraceMinutes === 'number' ? data.attendanceGraceMinutes : undefined,
    accessList: Array.isArray(data.accessList) ? (data.accessList as string[]) : null,
    readAccess: Array.isArray(data.readAccess) ? (data.readAccess as string[]) : null,
    accessRevision: typeof data.accessRevision === 'number' ? data.accessRevision : undefined,
    accessJob: (data.accessJob as OpsPlaceAccessJobProgress | null | undefined) ?? null,
    archived: data.archived === true,
    createdAt: data.createdAt as string,
    createdBy: data.createdBy as string,
    updatedAt: data.updatedAt as string,
    updatedBy: data.updatedBy as string,
  }
}

export function subscribeToPlaces(
  workspaceId: string,
  sector: OpsSector,
  callback: (places: OpsPlace[]) => void,
  onError?: (error: Error) => void,
  accessScope?: OpsSubscriptionAccessOptions,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }

  const placesRef = collection(firestoreDb, 'ops_places')

  if (accessScope?.isRestricted === true) {
    const callerUid = accessScope.callerUid?.trim()
    if (!callerUid) {
      onError?.(new Error('callerUid is required for restricted subscribeToPlaces'))
      return () => undefined
    }

    const qNull = query(
      placesRef,
      where('workspaceId', '==', workspaceId),
      where('sector', '==', sector),
      where('readAccess', '==', null),
    )
    const qUid = query(
      placesRef,
      where('workspaceId', '==', workspaceId),
      where('sector', '==', sector),
      where('readAccess', 'array-contains', callerUid),
    )

    let placesNull: OpsPlace[] = []
    let placesUid: OpsPlace[] = []

    const mergeAndEmit = () => {
      const map = new Map<string, OpsPlace>()
      for (const p of placesNull) {
        if (p.id) map.set(p.id, p)
      }
      for (const p of placesUid) {
        if (p.id) map.set(p.id, p)
      }
      callback(Array.from(map.values()))
    }

    const unsubNull = onSnapshot(
      qNull,
      (snap) => {
        placesNull = snap.docs.map(mapPlaceDoc)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    const unsubUid = onSnapshot(
      qUid,
      (snap) => {
        placesUid = snap.docs.map(mapPlaceDoc)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    return () => {
      unsubNull()
      unsubUid()
    }
  }

  const q = query(
    placesRef,
    where('workspaceId', '==', workspaceId),
    where('sector', '==', sector),
  )

  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map(mapPlaceDoc))
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}

/**
 * Subscribes to all places in a workspace across all sectors.
 */
export function subscribeToWorkspacePlaces(
  workspaceId: string,
  callback: (places: OpsPlace[]) => void,
  onError?: (error: Error) => void,
  accessScope?: OpsSubscriptionAccessOptions,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }
  const placesRef = collection(firestoreDb, 'ops_places')

  if (accessScope?.isRestricted === true) {
    const callerUid = accessScope.callerUid?.trim()
    if (!callerUid) {
      onError?.(new Error('callerUid is required for restricted subscribeToWorkspacePlaces'))
      return () => undefined
    }

    const qNull = query(
      placesRef,
      where('workspaceId', '==', workspaceId),
      where('readAccess', '==', null),
    )
    const qUid = query(
      placesRef,
      where('workspaceId', '==', workspaceId),
      where('readAccess', 'array-contains', callerUid),
    )

    let placesNull: OpsPlace[] = []
    let placesUid: OpsPlace[] = []

    const mergeAndEmit = () => {
      const map = new Map<string, OpsPlace>()
      for (const p of placesNull) {
        if (p.id) map.set(p.id, p)
      }
      for (const p of placesUid) {
        if (p.id) map.set(p.id, p)
      }
      callback(Array.from(map.values()))
    }

    const unsubNull = onSnapshot(
      qNull,
      (snap) => {
        placesNull = snap.docs.map(mapPlaceDoc)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    const unsubUid = onSnapshot(
      qUid,
      (snap) => {
        placesUid = snap.docs.map(mapPlaceDoc)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    return () => {
      unsubNull()
      unsubUid()
    }
  }

  const q = query(placesRef, where('workspaceId', '==', workspaceId))
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map(mapPlaceDoc))
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}

/**
 * Filter places that a member can see by effective access.
 * If readAccess is null, place is open to all members.
 * If readAccess is an array, memberUid must be in that array.
 */
export function getMemberVisiblePlaces(memberUid: string, places: OpsPlace[]): OpsPlace[] {
  return places.filter(
    (p) => p.readAccess === null || (Array.isArray(p.readAccess) && p.readAccess.includes(memberUid)),
  )
}

/**
 * Finds the nearest ancestor place that set an accessList.
 */
export function findInheritedPlaceAncestor(place: OpsPlace, allPlaces: OpsPlace[]): OpsPlace | null {
  const path = place.path ?? []
  const ancestors = allPlaces.filter((p) => path.includes(p.id!) && p.id !== place.id)
  const withList = ancestors.filter((p) => p.accessList !== null)
  return withList.length > 0 ? withList[withList.length - 1] : null
}

/**
 * Adds a member to a place's access list.
 */
export async function addMemberToPlaceAccess(
  workspaceId: string,
  place: OpsPlace,
  memberUid: string,
): Promise<SetPlaceAccessResult> {
  const opId = `op-add-member-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  const currentList = Array.isArray(place.accessList) ? place.accessList : []
  const nextList = Array.from(new Set([...currentList, memberUid]))
  return setPlaceAccess({
    workspaceId,
    placeId: place.id!,
    accessList: nextList,
    opId,
  })
}

/**
 * Removes a member from a place's access list.
 */
export async function removeMemberFromPlaceAccess(
  workspaceId: string,
  place: OpsPlace,
  memberUid: string,
): Promise<SetPlaceAccessResult> {
  const opId = `op-rem-member-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  const currentList = Array.isArray(place.accessList) ? place.accessList : []
  const nextList = currentList.filter((u) => u !== memberUid)
  return setPlaceAccess({
    workspaceId,
    placeId: place.id!,
    accessList: nextList,
    opId,
  })
}
